import { EventEmitter, Injectable } from '@angular/core';

import { BoundingBox, ImagePlaceholder
       } from 'collage-designer/stage/placeholder/image-placeholder';
import { Placeholder, PlaceholderType
       } from 'collage-designer/stage/placeholder/placeholder';
import { StageConstants } from 'core/constants';
import { Utils } from 'core/utils';

export interface ImageCroppingInformation
{
  imageId: string;
  crop: BoundingBox;
  targetId: string;
}

@Injectable()
export class ToolboxService
{
  public static readonly CANCELABLE_TOOLBOXES: Array<string> =
      ['ImageToolbox', 'TextToolbox'];

  public placeholder?: Placeholder;
  public targetChanged: EventEmitter<string> = new EventEmitter<string>();
  public placeholderChanged: EventEmitter<Placeholder> =
      new EventEmitter<Placeholder>();

  private activeToolboxName: string;
  private backup: any = undefined;

  public open(toolboxName: string): void
  {
    if (Utils.isSmallDevice()) {
      Utils.scrollToPosition(0, 0);
    }
    this.activeToolboxName = toolboxName;
    this.targetChanged.emit(this.activeToolboxName);
  }

  public edit(placeholder: Placeholder, toolboxName?: string): void
  {
    if (this.placeholder &&
        this.placeholder.get('id') !== placeholder.get('id')) {
      this.commitChanges();
    }

    this.placeholder = placeholder;
    this.placeholderChanged.emit(this.placeholder);
    if (!toolboxName) {
      toolboxName = this.determineActiveToolbox();
    }
    if (ToolboxService.CANCELABLE_TOOLBOXES.indexOf(toolboxName) > -1) {
      this.createBackupValues();
    }
    this.open(toolboxName);
  }

  public resetPlaceholder(): void
  {
    if (! this.placeholder) {
      return;
    }

    this.placeholder = undefined;
    this.placeholderChanged.emit(this.placeholder);
  }

  public getActiveToolboxName(): string
  {
    return this.activeToolboxName;
  }

  public hasActiveToolbox(): boolean
  {
    return !!this.activeToolboxName;
  }

  public commitChanges(): void
  {
    this.cleanUp();
  }

  public discardChanges(): void
  {
    this.restoreBackup();
    this.cleanUp();
  }

  public closeActiveToolbox(): void
  {
    if (this.activeToolboxName) {
      this.discardChanges();
    }
  }

  private cleanUp(): void
  {
    this.activeToolboxName = undefined;
    if (this.placeholder) {
      if (StageConstants.BLUR_PLACEHOLDER_AFTER_ACTION) {
        this.placeholder.blur(true);
      }
      this.placeholder = undefined;
      this.placeholderChanged.emit(this.placeholder);
    }
    this.backup = undefined;
    this.targetChanged.emit(this.activeToolboxName);
  }

  private determineActiveToolbox(): string
  {
    switch (this.placeholder.type) {
      case PlaceholderType.Image:
        return 'ImageToolbox';
      case PlaceholderType.Text:
        return 'TextToolbox';
      default:
        return undefined;
    }
  }

  private createBackupValues(): void
  {
    this.backup = {};
    if (this.placeholder.type === PlaceholderType.Image) {
      this.backup.bbox = (<ImagePlaceholder> this.placeholder).getBoundingBox();
    }
    else if (this.placeholder.type === PlaceholderType.Text) {
      // No backup needed at the moment. The old model is kept until the user
      // commits it by clicking 'accept'.
    }
  }

  private restoreBackup(): void
  {
    if (this.placeholder && this.backup) {
      if (this.placeholder.type === PlaceholderType.Image) {
        (<ImagePlaceholder> this.placeholder)
          .applyBoundingBox(this.backup.bbox);
      }
      else if (this.placeholder.type === PlaceholderType.Text) {
        // No backup needed at the moment. The old model is kept until the user
        // commits it by clicking 'accept'.
      }
    }
    this.backup = undefined;
  }
}
